import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Brain,
  Dumbbell,
  Apple,
  Footprints,
  Loader2,
  ChevronDown,
  ChevronUp,
  Target,
  Clock,
  Scale,
  Utensils,
  AlertCircle,
  Sparkles,
  Copy,
  Check,
  Trash2,
  History,
  Mail,
  Send,
  HelpCircle,
  HeartPulse,
  Pill,
  ShieldAlert,
  ShoppingCart
} from 'lucide-react';
import { trackUsage, FEATURES } from '../services/usageTracking';
import { AiGeneratingPlanSkeleton } from './Skeletons';


// localStorage keys for saved plans
const SAVED_PLANS_KEY = 'gym_notebook_ai_plans';

export default function AiPlanner({ onBack, isExpired = false, onNavigate }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('diet');
  const [loading, setLoading] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savedPlans, setSavedPlans] = useState({ diet: null, walking: null });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [addingGrocery, setAddingGrocery] = useState(false);
  const [groceryAdded, setGroceryAdded] = useState(false);
  
  // Track usage on mount
  useEffect(() => {
    trackUsage(FEATURES.AI_PLANNER);
  }, []);
  
  // User profile data (loaded from localStorage)
  const [userProfile, setUserProfile] = useState({
    bodyweight: '',
    weightUnit: 'kg',
    height: '',
    heightUnit: 'cm',
    age: '',
    gender: 'male',
    weightGoal: 'maintain'
  });
  
  // Exercise Planner Form
  const [exerciseForm, setExerciseForm] = useState({
    goal: 'build_muscle', // lose_weight, build_muscle, strength, endurance
    fitnessLevel: 'intermediate', // beginner, intermediate, advanced
    equipment: 'full_gym', // bodyweight, dumbbells, full_gym, home
    daysPerWeek: '4',
    minutesPerSession: '60',
    focusAreas: '', // e.g., "chest and arms", "full body", etc.
    injuries: ''
  });
  
  // Diet Planner Form
  const [dietForm, setDietForm] = useState({
    calorieGoal: '',
    dietaryRestrictions: '', // vegetarian, vegan, keto, etc.
    allergies: '',
    mealsPerDay: '3',
    snacks: true,
    cookingTime: 'moderate', // quick, moderate, elaborate
    cuisinePreference: ''
  });
  
  // Walking Planner Form
  const [walkingForm, setWalkingForm] = useState({
    currentSteps: '',
    goalSteps: '10000',
    daysPerWeek: '7',
    minutesAvailable: '60',
    terrain: 'mixed', // flat, hilly, mixed
    purpose: 'weight_loss' // weight_loss, cardiovascular, relaxation
  });

  // Health conditions (visible when medications enabled)
  const [medicationsEnabled] = useState(() => {
    const stored = localStorage.getItem('medications_enabled');
    return stored === null ? true : stored === 'true';
  });
  const [healthConditions, setHealthConditions] = useState(() => {
    return localStorage.getItem('ai_health_conditions') || '';
  });
  const [userMedications, setUserMedications] = useState([]);

  // Save health conditions to localStorage when changed
  useEffect(() => {
    localStorage.setItem('ai_health_conditions', healthConditions);
  }, [healthConditions]);

  // Fetch user's medications when medications are enabled
  useEffect(() => {
    if (!medicationsEnabled) return;
    const fetchMeds = async () => {
      try {
        const data = await api.get('/medications');
        if (data?.medications) {
          setUserMedications(data.medications);
        }
      } catch (e) {
        console.log('[AiPlanner] Could not fetch medications:', e.message);
      }
    };
    fetchMeds();
  }, [medicationsEnabled]);
  
  // Load user profile and saved plans from localStorage and backend on mount
  useEffect(() => {
    const bodyweight = localStorage.getItem('gym_notebook_persistent_bodyweight') || '';
    const weightUnit = localStorage.getItem('gym_notebook_persistent_weight_unit') || 'kg';
    const height = localStorage.getItem('gym_notebook_persistent_height') || '';
    const heightUnit = localStorage.getItem('gym_notebook_persistent_height_unit') || 'cm';
    const age = localStorage.getItem('gym_notebook_persistent_age') || '';
    const gender = localStorage.getItem('gym_notebook_persistent_gender') || 'male';
    const weightGoal = localStorage.getItem('gym_notebook_weight_goal') || 'maintain';
    
    setUserProfile({ bodyweight, weightUnit, height, heightUnit, age, gender, weightGoal });
    
    // Pre-populate calorie goal based on weight goal
    if (weightGoal === 'lose') {
      setDietForm(prev => ({ ...prev, calorieGoal: '' })); // Will be calculated
    }
    
    // Load saved AI plans from backend first, then localStorage as fallback
    const loadPlans = async () => {
      try {
        const response = await api.get('/ai/plans');
        if (response?.plans && Array.isArray(response.plans) && response.plans.length > 0) {
          // Transform array to object keyed by plan_type
          const plansObj = {};
          response.plans.forEach(plan => {
            plansObj[plan.plan_type] = {
              plan: plan.plan_content,
              plan_id: plan.plan_id,
              generatedAt: plan.created_at
            };
          });
          
          setSavedPlans(plansObj);
          // Load the plan for the current active tab
          if (plansObj[activeTab]) {
            setGeneratedPlan(plansObj[activeTab].plan);
          } else if (plansObj.exercise) {
            setGeneratedPlan(plansObj.exercise.plan);
          } else {
            // Load the first available plan
            const firstPlan = Object.values(plansObj)[0];
            if (firstPlan) {
              setGeneratedPlan(firstPlan.plan);
            }
          }
          
          // Also save to localStorage for offline access
          localStorage.setItem(SAVED_PLANS_KEY, JSON.stringify(plansObj));
          
          console.log('[AiPlanner] Plans loaded from cloud:', Object.keys(plansObj));
          return;
        }
      } catch (e) {
        console.log('[AiPlanner] Cloud plans not available:', e.message);
      }
      
      // Fallback to localStorage
      const savedPlansStr = localStorage.getItem(SAVED_PLANS_KEY);
      if (savedPlansStr) {
        try {
          const plans = JSON.parse(savedPlansStr);
          setSavedPlans(plans);
          if (plans[activeTab]) {
            setGeneratedPlan(plans[activeTab].plan);
          } else if (plans.exercise) {
            setGeneratedPlan(plans.exercise.plan);
          }
        } catch (e) {
          console.error('Error loading saved AI plans:', e);
        }
      }
    };
    
    loadPlans();
  }, []);
  
  // When tab changes, load the saved plan for that tab
  useEffect(() => {
    if (savedPlans[activeTab]) {
      setGeneratedPlan(savedPlans[activeTab].plan);
    } else {
      setGeneratedPlan(null);
    }
  }, [activeTab, savedPlans]);
  
  // Save plan to backend and localStorage when generated
  const savePlanToStorage = async (planType, plan) => {
    const updatedPlans = {
      ...savedPlans,
      [planType]: {
        plan,
        generatedAt: new Date().toISOString(),
        formData: planType === 'exercise' ? exerciseForm : planType === 'diet' ? dietForm : walkingForm
      }
    };
    setSavedPlans(updatedPlans);
    
    // Save to backend
    try {
      await api.post('/ai/plans/save', {
        plan_type: planType,
        plan: plan,
        form_data: planType === 'exercise' ? exerciseForm : planType === 'diet' ? dietForm : walkingForm
      });
      console.log('[AiPlanner] Plan saved to cloud:', planType);
    } catch (e) {
      console.error('[AiPlanner] Error saving plan to cloud:', e.message);
    }
    
    // Also save to localStorage as backup
    try {
      localStorage.setItem(SAVED_PLANS_KEY, JSON.stringify(updatedPlans));
    } catch (e) {
      console.error('[AiPlanner] Error saving plan to localStorage:', e);
      if (e.name === 'QuotaExceededError') {
        toast.error('Local storage is full. Plan saved to cloud only.');
      }
    }
  };

  // Generate AI Plan
  const generatePlan = async () => {
    setLoading(true);
    setGeneratedPlan(null);
    
    try {
      let requestBody = {
        planType: activeTab,
        userProfile
      };
      
      if (activeTab === 'exercise') {
        requestBody.formData = exerciseForm;
      } else if (activeTab === 'diet') {
        requestBody.formData = dietForm;
      } else if (activeTab === 'walking') {
        requestBody.formData = walkingForm;
      }

      // Include health conditions and medications when enabled
      if (medicationsEnabled) {
        requestBody.healthConditions = healthConditions;
        requestBody.medications = userMedications.map(m => ({
          name: m.name,
          dosage: m.dosage,
          frequency: m.frequency
        }));
      }
      
      const data = await api.post('/ai/generate-plan', requestBody);
      
      if (data && data.plan) {
        setGeneratedPlan(data.plan);
        setGroceryAdded(false);
        savePlanToStorage(activeTab, data.plan);
        toast.success('Plan generated and saved!');
      } else {
        throw new Error('No plan received from server');
      }
    } catch (err) {
      console.error('Error generating plan:', err);
      const errorMessage = err?.response?.data?.detail || err.message || '';
      
      // Provide user-friendly error messages
      if (errorMessage.includes('503') || errorMessage.includes('temporarily') || errorMessage.includes('busy') || errorMessage.includes('unavailable')) {
        toast.error('AI service is temporarily unavailable. Please try again in a moment.');
      } else if (errorMessage.includes('504') || errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
        toast.error('Request timed out. The AI is taking longer than usual. Please try again.');
      } else if (errorMessage.includes('502') || errorMessage.includes('not responding')) {
        toast.error('AI service is temporarily busy. Please wait a moment and try again.');
      } else if (errorMessage.includes('quota') || errorMessage.includes('balance')) {
        toast.error('AI service quota exceeded. Please try again later.');
      } else if (errorMessage.includes('500')) {
        toast.error('Server error. Please try again in a moment.');
      } else {
        toast.error(errorMessage || 'Failed to generate plan. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Copy plan to clipboard
  const copyPlan = () => {
    if (generatedPlan) {
      navigator.clipboard.writeText(generatedPlan);
      setCopied(true);
      toast.success('Plan copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  // Clear the current plan
  const clearPlan = async () => {
    const updatedPlans = { ...savedPlans, [activeTab]: null };
    setSavedPlans(updatedPlans);
    setGeneratedPlan(null);
    setGroceryAdded(false);
    
    // Delete from backend
    try {
      await api.delete(`/ai/plans/${activeTab}`);
    } catch (e) {
      console.log('[AiPlanner] Error deleting from cloud:', e.message);
    }
    
    // Update localStorage
    localStorage.setItem(SAVED_PLANS_KEY, JSON.stringify(updatedPlans));
    toast.success('Plan cleared');
  };

  // Extract grocery items from AI diet plan text
  const extractGroceryItems = (planText) => {
    if (!planText) return [];
    const items = [];
    const lines = planText.split('\n');
    let inGrocerySection = false;
    let grocerySectionLevel = 0; // track heading level (## = 2, ### = 3)
    for (const line of lines) {
      const trimmed = line.trim();
      const lower = trimmed.toLowerCase();
      // Detect grocery/shopping list section headers
      const groceryHeader = lower.match(/^(#{1,4})\s.*(grocery|shopping list|ingredients to buy|weekly shop)/i);
      if (groceryHeader) {
        inGrocerySection = true;
        grocerySectionLevel = groceryHeader[1].length; // e.g. 2 for ##
        continue;
      }
      // Stop only at same-level or higher header (not sub-headers within grocery)
      if (inGrocerySection) {
        const headerMatch = trimmed.match(/^(#{1,4})\s/);
        if (headerMatch) {
          const level = headerMatch[1].length;
          if (level <= grocerySectionLevel) {
            // Same or higher level header = new section, exit grocery
            inGrocerySection = false;
            continue;
          }
          // Sub-header within grocery (e.g. ### Proteins) — skip the header line itself
          continue;
        }
      }
      // Extract bullet items in grocery section
      if (inGrocerySection && trimmed.match(/^[-*•]\s+(.+)/)) {
        const match = trimmed.match(/^[-*•]\s+(.+)/);
        if (match) {
          const item = match[1].replace(/\*\*/g, '').trim();
          if (item.length > 1 && item.length < 100) items.push(item);
        }
      }
    }
    return items;
  };

  // Add diet plan grocery items to FridgeOracle grocery list
  const addGroceryToFridge = async () => {
    if (!generatedPlan || activeTab !== 'diet') return;
    setAddingGrocery(true);
    try {
      const newItems = extractGroceryItems(generatedPlan);
      if (newItems.length === 0) {
        toast.error('No grocery items found in the diet plan');
        return;
      }
      // Fetch existing grocery list
      let existingItems = [];
      try {
        const data = await api.get('/fridge/grocery');
        existingItems = data?.items || [];
      } catch (e) { /* no existing list */ }
      // Merge: add new items that don't already exist (case-insensitive)
      const existingLower = new Set(existingItems.map(i => (typeof i === 'string' ? i : i.name || '').toLowerCase()));
      const merged = [...existingItems];
      let addedCount = 0;
      for (const item of newItems) {
        if (!existingLower.has(item.toLowerCase())) {
          merged.push({ name: item, bought: false });
          existingLower.add(item.toLowerCase());
          addedCount++;
        }
      }
      // Save merged list
      await api.put('/fridge/grocery', { items: merged });
      setGroceryAdded(true);
      toast.success(`Added ${addedCount} item${addedCount !== 1 ? 's' : ''} to FridgeOracle grocery list`);
    } catch (err) {
      console.error('Error adding grocery to FridgeOracle:', err);
      toast.error('Failed to add grocery items. Please try again.');
    } finally {
      setAddingGrocery(false);
    }
  };
  
  // Send plan to email as PDF
  const sendPlanToEmail = async () => {
    if (!generatedPlan) return;
    
    // Get user email from auth context
    const userEmail = user?.email;
    if (!userEmail) {
      toast.error('Email not found. Please sign in again.');
      return;
    }
    
    setSendingEmail(true);
    setEmailSent(false);
    
    try {
      await api.post('/ai/send-plan-email', {
        planType: activeTab,
        planContent: generatedPlan,
        email: userEmail
      });
      
      setEmailSent(true);
      toast.success(`Plan sent to ${userEmail}!`);
      setTimeout(() => setEmailSent(false), 3000);
    } catch (err) {
      console.error('Error sending plan email:', err);
      toast.error(err.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Get the timestamp for the current plan
  const getPlanTimestamp = () => {
    if (savedPlans[activeTab]?.generatedAt) {
      const date = new Date(savedPlans[activeTab].generatedAt);
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return null;
  };
  
  // Tab content components
  const renderExerciseForm = () => (
    <div className="space-y-4">
      {/* Goal */}
      <div className="space-y-2">
        <Label className="text-white flex items-center gap-2">
          <Target className="h-4 w-4 text-green-400" />
          Primary Goal
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'lose_weight', label: 'Lose Weight', color: 'red' },
            { value: 'build_muscle', label: 'Build Muscle', color: 'green' },
            { value: 'strength', label: 'Strength', color: 'orange' },
            { value: 'endurance', label: 'Endurance', color: 'blue' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setExerciseForm(prev => ({ ...prev, goal: option.value }))}
              className={`p-3 rounded-xl border-2 transition-all ${
                exerciseForm.goal === option.value
                  ? `border-${option.color}-500 bg-${option.color}-500/20 text-${option.color}-400`
                  : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Fitness Level */}
      <div className="space-y-2">
        <Label className="text-white">Fitness Level</Label>
        <div className="grid grid-cols-3 gap-2">
          {['beginner', 'intermediate', 'advanced'].map(level => (
            <button
              key={level}
              onClick={() => setExerciseForm(prev => ({ ...prev, fitnessLevel: level }))}
              className={`p-2 rounded-xl border-2 capitalize transition-all text-sm ${
                exerciseForm.fitnessLevel === level
                  ? 'border-green-500 bg-green-500/20 text-green-400'
                  : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>
      
      {/* Equipment */}
      <div className="space-y-2">
        <Label className="text-white flex items-center gap-2">
          <Dumbbell className="h-4 w-4 text-orange-400" />
          Available Equipment
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'bodyweight', label: 'Bodyweight Only' },
            { value: 'dumbbells', label: 'Dumbbells' },
            { value: 'home', label: 'Home Gym' },
            { value: 'full_gym', label: 'Full Gym' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setExerciseForm(prev => ({ ...prev, equipment: option.value }))}
              className={`p-2 rounded-xl border-2 transition-all ${
                exerciseForm.equipment === option.value
                  ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                  : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Days per week & Duration */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white h-5 flex items-center">Days/Week</Label>
          <Input
            type="number"
            min="1"
            max="7"
            value={exerciseForm.daysPerWeek}
            onChange={(e) => setExerciseForm(prev => ({ ...prev, daysPerWeek: e.target.value }))}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white h-5 flex items-center">Minutes/Session</Label>
          <Input
            type="number"
            min="15"
            max="120"
            value={exerciseForm.minutesPerSession}
            onChange={(e) => setExerciseForm(prev => ({ ...prev, minutesPerSession: e.target.value }))}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>
      
      {/* Focus Areas */}
      <div className="space-y-2">
        <Label className="text-white">Focus Areas (optional)</Label>
        <Input
          placeholder="e.g., chest and arms, legs, full body..."
          value={exerciseForm.focusAreas}
          onChange={(e) => setExerciseForm(prev => ({ ...prev, focusAreas: e.target.value }))}
          className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
        />
      </div>
      
      {/* Injuries */}
      <div className="space-y-2">
        <Label className="text-white flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-400" />
          Injuries/Limitations (optional)
        </Label>
        <Input
          placeholder="e.g., bad knee, lower back issues..."
          value={exerciseForm.injuries}
          onChange={(e) => setExerciseForm(prev => ({ ...prev, injuries: e.target.value }))}
          className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
        />
      </div>
    </div>
  );
  
  const renderDietForm = () => (
    <div className="space-y-4">
      {/* Calorie Goal */}
      <div className="space-y-2">
        <Label className="text-white flex items-center gap-2">
          <Scale className="h-4 w-4 text-orange-400" />
          Daily Calorie Goal (optional)
        </Label>
        <Input
          type="number"
          placeholder="Leave empty to calculate based on your profile"
          value={dietForm.calorieGoal}
          onChange={(e) => setDietForm(prev => ({ ...prev, calorieGoal: e.target.value }))}
          className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
        />
        <p className="text-xs text-slate-500">
          Leave empty and we'll calculate based on your weight goal: {userProfile.weightGoal}
        </p>
      </div>
      
      {/* Dietary Restrictions */}
      <div className="space-y-2">
        <Label className="text-white flex items-center gap-2">
          <Utensils className="h-4 w-4 text-green-400" />
          Dietary Restrictions
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {['none', 'vegetarian', 'vegan', 'keto', 'paleo', 'gluten-free'].map(diet => (
            <button
              key={diet}
              onClick={() => setDietForm(prev => ({ ...prev, dietaryRestrictions: diet === 'none' ? '' : diet }))}
              className={`p-2 rounded-xl border-2 capitalize transition-all ${
                (diet === 'none' && !dietForm.dietaryRestrictions) || dietForm.dietaryRestrictions === diet
                  ? 'border-green-500 bg-green-500/20 text-green-400'
                  : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
              }`}
            >
              {diet}
            </button>
          ))}
        </div>
      </div>
      
      {/* Allergies */}
      <div className="space-y-2">
        <Label className="text-white flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-400" />
          Food Allergies (optional)
        </Label>
        <Input
          placeholder="e.g., nuts, dairy, shellfish..."
          value={dietForm.allergies}
          onChange={(e) => setDietForm(prev => ({ ...prev, allergies: e.target.value }))}
          className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
        />
      </div>
      
      {/* Meals & Snacks */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white h-5 flex items-center">Meals Per Day</Label>
          <Input
            type="number"
            min="2"
            max="6"
            value={dietForm.mealsPerDay}
            onChange={(e) => setDietForm(prev => ({ ...prev, mealsPerDay: e.target.value }))}
            className="bg-slate-800 border-slate-700 text-white h-10"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white h-5 flex items-center">Include Snacks</Label>
          <div className="flex gap-2 h-10">
            <button
              onClick={() => setDietForm(prev => ({ ...prev, snacks: true }))}
              className={`flex-1 rounded-xl border-2 transition-all ${
                dietForm.snacks
                  ? 'border-green-500 bg-green-500/20 text-green-400'
                  : 'border-slate-700 bg-slate-800/50 text-slate-300'
              }`}
            >
              Yes
            </button>
            <button
              onClick={() => setDietForm(prev => ({ ...prev, snacks: false }))}
              className={`flex-1 rounded-xl border-2 transition-all ${
                !dietForm.snacks
                  ? 'border-red-500 bg-red-500/20 text-red-400'
                  : 'border-slate-700 bg-slate-800/50 text-slate-300'
              }`}
            >
              No
            </button>
          </div>
        </div>
      </div>
      
      {/* Cooking Time */}
      <div className="space-y-2">
        <Label className="text-white flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-400" />
          Cooking Time Available
        </Label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'quick', label: 'Quick (<30min)' },
            { value: 'moderate', label: 'Moderate' },
            { value: 'elaborate', label: 'Elaborate' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setDietForm(prev => ({ ...prev, cookingTime: option.value }))}
              className={`p-2 rounded-xl border-2 transition-all text-sm ${
                dietForm.cookingTime === option.value
                  ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                  : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Cuisine Preference */}
      <div className="space-y-2">
        <Label className="text-white">Cuisine Preference (optional)</Label>
        <Input
          placeholder="e.g., Mediterranean, Asian, Mexican..."
          value={dietForm.cuisinePreference}
          onChange={(e) => setDietForm(prev => ({ ...prev, cuisinePreference: e.target.value }))}
          className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
        />
      </div>

      {/* Health Conditions - shown when Medications enabled */}
      {medicationsEnabled && (
        <div className="space-y-3 mt-2 p-4 rounded-xl border-2 border-teal-500/30 bg-teal-500/5">
          <Label className="text-white flex items-center gap-2">
            <HeartPulse className="h-4 w-4 text-teal-400" />
            Health Conditions
          </Label>
          <Textarea
            placeholder="e.g., Type 2 Diabetes, Hypertension, High cholesterol..."
            value={healthConditions}
            onChange={(e) => setHealthConditions(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white placeholder-slate-500 min-h-[60px]"
            data-testid="health-conditions-input"
          />
          {userMedications.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-slate-400 flex items-center gap-1">
                <Pill className="h-3 w-3 text-teal-400" />
                Your medications ({userMedications.length}):
              </p>
              <div className="flex flex-wrap gap-1">
                {userMedications.map((med, i) => (
                  <span key={i} className="text-[10px] bg-teal-500/20 text-teal-300 px-2 py-0.5 rounded-full">
                    {med.name} {med.dosage}
                  </span>
                ))}
              </div>
            </div>
          )}
          <p className="text-[10px] text-slate-500 flex items-center gap-1">
            <ShieldAlert className="h-3 w-3" />
            AI will consider your health conditions and medications when creating the plan. This is not medical advice.
          </p>
        </div>
      )}
    </div>
  );
  
  const renderWalkingForm = () => (
    <div className="space-y-4">
      {/* Current & Goal Steps */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white h-5 flex items-center">Current Daily Steps</Label>
          <Input
            type="number"
            placeholder="e.g., 3000"
            value={walkingForm.currentSteps}
            onChange={(e) => setWalkingForm(prev => ({ ...prev, currentSteps: e.target.value }))}
            className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white h-5 flex items-center">Step Goal</Label>
          <Input
            type="number"
            value={walkingForm.goalSteps}
            onChange={(e) => setWalkingForm(prev => ({ ...prev, goalSteps: e.target.value }))}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>
      
      {/* Days & Time */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white h-5 flex items-center">Days Per Week</Label>
          <Input
            type="number"
            min="1"
            max="7"
            value={walkingForm.daysPerWeek}
            onChange={(e) => setWalkingForm(prev => ({ ...prev, daysPerWeek: e.target.value }))}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white h-5 flex items-center">Minutes Available</Label>
          <Input
            type="number"
            min="15"
            max="180"
            value={walkingForm.minutesAvailable}
            onChange={(e) => setWalkingForm(prev => ({ ...prev, minutesAvailable: e.target.value }))}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>
      
      {/* Terrain */}
      <div className="space-y-2">
        <Label className="text-white">Terrain Type</Label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'flat', label: 'Flat' },
            { value: 'hilly', label: 'Hilly' },
            { value: 'mixed', label: 'Mixed' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setWalkingForm(prev => ({ ...prev, terrain: option.value }))}
              className={`p-2 rounded-xl border-2 capitalize transition-all ${
                walkingForm.terrain === option.value
                  ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                  : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Purpose */}
      <div className="space-y-2">
        <Label className="text-white flex items-center gap-2">
          <Target className="h-4 w-4 text-green-400" />
          Primary Purpose
        </Label>
        <div className="grid grid-cols-1 gap-2">
          {[
            { value: 'weight_loss', label: 'Weight Loss & Fat Burning', color: 'red' },
            { value: 'cardiovascular', label: 'Cardiovascular Health', color: 'blue' },
            { value: 'relaxation', label: 'Stress Relief & Relaxation', color: 'green' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setWalkingForm(prev => ({ ...prev, purpose: option.value }))}
              className={`p-3 rounded-xl border-2 transition-all text-left ${
                walkingForm.purpose === option.value
                  ? `border-${option.color}-500 bg-${option.color}-500/20 text-${option.color}-400`
                  : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Health Conditions - shown when Medications enabled */}
      {medicationsEnabled && (
        <div className="space-y-3 mt-2 p-4 rounded-xl border-2 border-teal-500/30 bg-teal-500/5">
          <Label className="text-white flex items-center gap-2">
            <HeartPulse className="h-4 w-4 text-teal-400" />
            Health Conditions
          </Label>
          <Textarea
            placeholder="e.g., Type 2 Diabetes, Hypertension, Asthma..."
            value={healthConditions}
            onChange={(e) => setHealthConditions(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white placeholder-slate-500 min-h-[60px]"
            data-testid="health-conditions-walking-input"
          />
          {userMedications.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-slate-400 flex items-center gap-1">
                <Pill className="h-3 w-3 text-teal-400" />
                Your medications ({userMedications.length}):
              </p>
              <div className="flex flex-wrap gap-1">
                {userMedications.map((med, i) => (
                  <span key={i} className="text-[10px] bg-teal-500/20 text-teal-300 px-2 py-0.5 rounded-full">
                    {med.name} {med.dosage}
                  </span>
                ))}
              </div>
            </div>
          )}
          <p className="text-[10px] text-slate-500 flex items-center gap-1">
            <ShieldAlert className="h-3 w-3" />
            AI will consider your health and medications when planning walking intensity. This is not medical advice.
          </p>
        </div>
      )}
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none ios-header" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', border: 'none' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Brain className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-lg font-heading font-bold text-white">AI Planner</h1>
          </div>
          <span className="text-xs text-violet-400 bg-violet-500/20 px-3 py-1.5 rounded-full border border-violet-500/30">
            Powered by Claude
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowOnboarding(true)} 
            className="text-slate-400 hover:text-violet-400"
            title="Help & Tutorial"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-6 max-w-lg pb-24">
        {/* User Profile Summary */}
        {(userProfile.bodyweight || userProfile.age) && (
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <span className="text-white font-medium">Your Profile:</span>
                {userProfile.bodyweight && <span>{userProfile.bodyweight} {userProfile.weightUnit}</span>}
                {userProfile.height && <span>• {userProfile.height} {userProfile.heightUnit}</span>}
                {userProfile.age && <span>• {userProfile.age} years</span>}
                {userProfile.gender && <span>• {userProfile.gender}</span>}
                <span className={`ml-auto px-2 py-0.5 rounded text-xs ${
                  userProfile.weightGoal === 'lose' ? 'bg-red-500/20 text-red-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {userProfile.weightGoal === 'lose' ? 'Lose Weight' : 'Maintain'}
                </span>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Tab Selector - Diet and Walking only */}
        <div className="flex gap-2 mb-6 p-1.5 bg-slate-900/80 rounded-2xl border border-slate-800">
          <button
            onClick={() => { setActiveTab('diet'); setGeneratedPlan(null); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all ${
              activeTab === 'diet'
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
            data-testid="ai-tab-diet"
          >
            <Apple className="h-5 w-5" />
            <span className="font-medium">Diet</span>
          </button>
          <button
            onClick={() => { setActiveTab('walking'); setGeneratedPlan(null); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all ${
              activeTab === 'walking'
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
            data-testid="ai-tab-walking"
          >
            <Footprints className="h-5 w-5" />
            <span className="font-medium">Walking</span>
          </button>
        </div>
        
        {/* Form Card */}
        <Card className="bg-slate-900/80 border-slate-800 rounded-2xl mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg font-heading">
              {activeTab === 'diet' && <><Apple className="h-5 w-5 text-amber-400" /> Diet Plan</>}
              {activeTab === 'walking' && <><Footprints className="h-5 w-5 text-blue-400" /> Walking Plan</>}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {activeTab === 'diet' && renderDietForm()}
            {activeTab === 'walking' && renderWalkingForm()}
            
            {/* Generate Button */}
            <Button
              onClick={generatePlan}
              disabled={loading}
              className="w-full mt-6 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white py-6 text-lg font-semibold rounded-xl shadow-lg shadow-violet-500/20"
              data-testid="generate-plan-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Generating Plan...
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Generate AI Plan
                </>
              )}
            </Button>
          </CardContent>
        </Card>
        
        {/* AI Generating Skeleton */}
        {loading && (
          <AiGeneratingPlanSkeleton />
        )}
        
        {/* Generated Plan */}
        {generatedPlan && (
          <Card className="bg-slate-800/50 border-slate-700 border-2 border-purple-500/30">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2 text-lg">
                    <Sparkles className="h-5 w-5 text-purple-400" />
                    Your Personalized Plan
                  </CardTitle>
                  {getPlanTimestamp() && (
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                      <History className="h-3 w-3" />
                      Generated: {getPlanTimestamp()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {activeTab === 'diet' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={addGroceryToFridge}
                      disabled={addingGrocery || groceryAdded}
                      className="text-slate-400 hover:text-emerald-400"
                      data-testid="add-grocery-fridge-btn"
                      title="Add grocery list to FridgeOracle"
                    >
                      {addingGrocery ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : groceryAdded ? (
                        <Check className="h-4 w-4 text-green-400" />
                      ) : (
                        <ShoppingCart className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={sendPlanToEmail}
                    disabled={sendingEmail}
                    className="text-slate-400 hover:text-blue-400"
                    data-testid="email-plan-btn"
                    title="Send to email as PDF"
                  >
                    {sendingEmail ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : emailSent ? (
                      <Check className="h-4 w-4 text-green-400" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyPlan}
                    className="text-slate-400 hover:text-white"
                    data-testid="copy-plan-btn"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearPlan}
                    className="text-red-400/70 hover:text-red-400"
                    data-testid="clear-plan-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="prose prose-invert prose-sm max-w-none">
                <div 
                  className="text-slate-300 whitespace-pre-wrap leading-relaxed"
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(
                      generatedPlan
                      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold text-purple-400 mt-4 mb-2">$1</h3>')
                      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-purple-300 mt-4 mb-2">$1</h2>')
                      .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold text-white mt-4 mb-2">$1</h1>')
                      .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
                      .replace(/^(\d+)\. (.*$)/gm, '<li class="ml-4"><span class="text-purple-400 font-bold">$1.</span> $2</li>'),
                      { ALLOWED_TAGS: ['strong', 'h1', 'h2', 'h3', 'li', 'span', 'br', 'p', 'ul', 'ol'], ALLOWED_ATTR: ['class'] }
                    )
                  }}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
